import React, { useEffect, useRef, useState } from "react";
import "./resellerStudio.css";

type Piece = { id: string; name: string };

export type LeakResult = {
  found: boolean;
  reason?: "NO_PHOTO_MATCH" | "NO_MARK";
  recipientName?: string;
  watermarkCode?: string;
  phoneEnding?: string | null;
  linkRole?: "DIRECT" | "RESELLER" | "CLIENT";
  viaReseller?: string | null;
  createdAt?: string;
  disabled?: boolean;
  confidence?: number;
  photosChecked?: number;
};

const base = () => (process.env.REACT_APP_SERVERURL || "").replace(/\/+$/, "");
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("token") || ""}` });

const ROLE: Record<string, string> = {
  DIRECT: "Buyer link",
  RESELLER: "Reseller link",
  CLIENT: "A reseller's client",
};

export async function traceLeak(catalogueId: string, productUniqueId: string, file: File) {
  const body = new FormData();
  body.append("file", file);
  body.append("productUniqueId", productUniqueId);
  const res = await fetch(`${base()}/catalogue-media/${encodeURIComponent(catalogueId)}/trace`, {
    method: "POST",
    headers: auth(),
    body,
  });
  const json = await res.json().catch(() => ({}));
  return { ok: Boolean(json?.success), message: String(json?.message || ""), data: json?.data as LeakResult | undefined };
}

/**
 * Studio: upload a screenshot or saved photo that got out, pick the piece it shows, and see whose
 * link it came from. Reads the invisible per-link mark every photo carries.
 */
const LeakCheckCard: React.FC<{ catalogueId: string }> = ({ catalogueId }) => {
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [pieceId, setPieceId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<LeakResult | null>(null);
  const [message, setMessage] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The API pages at most 100 at a time.
        const rows: any[] = [];
        for (let offset = 0; offset < 5; offset++) {
          const res = await fetch(
            `${base()}/catalouge/${encodeURIComponent(catalogueId)}/products?offset=${offset}&limit=100&sortBy=name&sortOrder=asc`,
            { headers: { ...auth(), "Content-Type": "application/json" } }
          );
          const json = await res.json().catch(() => ({}));
          const page: any[] = Array.isArray(json?.data?.products) ? json.data.products : [];
          rows.push(...page);
          if (page.length < 100) break;
        }
        const list = rows
          .map((p) => ({
            id: String(p?.productUniqueId || "").trim(),
            name: String(p?.name || p?.productName || p?.productUniqueId || "").trim(),
          }))
          .filter((p) => p.id);
        if (!cancelled) {
          setPieces(list);
          setPieceId((prev) => prev || list[0]?.id || "");
        }
      } catch {
        if (!cancelled) setPieces([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [catalogueId]);

  const check = async () => {
    if (!file || !pieceId) return;
    setBusy(true);
    setResult(null);
    setMessage("");
    try {
      const res = await traceLeak(catalogueId, pieceId, file);
      if (!res.ok || !res.data) {
        setMessage(res.message || "Could not check this image.");
      } else {
        setResult(res.data);
        setMessage(res.message);
      }
    } catch {
      setMessage("Could not check this image.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hkp-studio-tree hkp-leak" aria-labelledby="leak-check-title">
      <header className="hkp-studio-tree-head">
        <h3 id="leak-check-title">Check a leaked image</h3>
      </header>
      <p className="hkp-studio-tree-muted">
        Every photo a buyer sees carries an invisible mark for their link. Upload a screenshot or saved photo that
        reached someone it shouldn't have, choose the piece it shows, and see whose link it came from.
      </p>
      <div className="hkp-leak-form">
        <label className="hkp-leak-field">
          <span>Piece</span>
          <select value={pieceId} onChange={(e) => setPieceId(e.target.value)} disabled={!pieces.length}>
            {pieces.length ? null : <option value="">No pieces in this catalogue</option>}
            {pieces.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="hkp-leak-field">
          <span>Image</span>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setResult(null);
              setMessage("");
            }}
          />
        </label>
        <button
          type="button"
          className="hkp-link-ready-btn"
          onClick={() => void check()}
          disabled={!file || !pieceId || busy}
        >
          {busy ? "Checking…" : "Check image"}
        </button>
      </div>
      {busy ? (
        <p className="hkp-studio-tree-muted" role="status">
          Matching the image against this piece's photos. This takes a few seconds.
        </p>
      ) : null}
      {result?.found ? (
        <div className="hkp-leak-result is-found" role="status">
          <strong>
            From {result.recipientName || "a buyer"}'s link
            {result.watermarkCode ? <span className="hkp-leak-code"> #{result.watermarkCode}</span> : null}
          </strong>
          <span>
            {[
              result.linkRole ? ROLE[result.linkRole] : null,
              result.viaReseller ? `shared by ${result.viaReseller}` : null,
              result.phoneEnding ? `mobile ending ${result.phoneEnding}` : null,
              result.createdAt ? `created ${new Date(result.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}` : null,
              result.disabled ? "link already disabled" : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </span>
        </div>
      ) : result ? (
        <div className="hkp-leak-result" role="status">
          <strong>No link found</strong>
          <span>
            {result.reason === "NO_PHOTO_MATCH"
              ? "The image doesn't clearly show a photo of this piece. Try another piece, or a screenshot where the photo is shown whole."
              : "No link's mark was found. It may be from before marks were added, or edited heavily."}
          </span>
        </div>
      ) : message ? (
        <p className="hkp-studio-tree-error">{message}</p>
      ) : null}
    </section>
  );
};

export default LeakCheckCard;
